import { el } from '../panels.js';
import type { PhoneTexts } from './texts-phone.js';
import { clampToStep, formatValue, parseValue, stepValue, type RangeSpec } from './values.js';

/**
 * Zazzle's mobile stepper: "Manje · [n] · Više", with a slider under it for
 * ranges wide enough to be worth dragging.
 *
 * It is a phone control, not the desktop one: the desktop panels use
 * `controls.createStepper` (− [n] +, label to the left), while a phone shows
 * two thumb-sized text buttons around a big number. Both end in the same
 * place — one `onCommit` per press, `onInput` only while the slider moves, so
 * a drag across the whole range is one undo step and not sixty rasterisations.
 */
export interface PhoneStepperOptions {
  texts: PhoneTexts;
  /** The accessible name of the number field ("Veličina (mm)"). */
  ariaLabel: string;
  range: RangeSpec;
  value: number;
  /** Draw the slider under the stepper; on by default. */
  slider?: boolean;
  /** Every frame of the slider drag: the caller previews. */
  onInput?(value: number): void;
  /** A button press, a typed value, or the end of a slider drag. */
  onCommit(value: number): void;
}

export interface PhoneStepperHandle {
  element: HTMLElement;
  /** Re-reads a value the caller changed elsewhere (undo, a reset button). */
  setValue(value: number): void;
  value(): number;
  destroy(): void;
}

export function createPhoneStepper(options: PhoneStepperOptions): PhoneStepperHandle {
  const range = options.range;
  let current = clampToStep(options.value, range);

  const wrap = el('div', 'pzd-ph-stepper-wrap');
  const row = el('div', 'pzd-ph-stepper');
  const less = el('button', 'pzd-ph-step-btn', options.texts.less);
  less.type = 'button';
  less.setAttribute('aria-label', `${options.ariaLabel}: ${options.texts.less}`);
  const valueBox = el('div', 'pzd-ph-step-val');
  const field = el('input');
  field.type = 'text';
  field.inputMode = 'decimal';
  field.autocomplete = 'off';
  field.setAttribute('aria-label', options.ariaLabel);
  field.value = formatValue(current, range.decimals);
  valueBox.append(field);
  if (range.unit) valueBox.append(el('span', 'pzd-ph-step-unit', range.unit));
  const more = el('button', 'pzd-ph-step-btn', options.texts.moreStep);
  more.type = 'button';
  more.setAttribute('aria-label', `${options.ariaLabel}: ${options.texts.moreStep}`);
  row.append(less, valueBox, more);
  wrap.append(row);

  const slide = el('input', 'pzd-ph-range');
  slide.type = 'range';
  slide.min = String(range.min);
  slide.max = String(range.max);
  slide.step = String(range.sliderStep ?? range.step);
  slide.value = String(current);
  slide.setAttribute('aria-label', options.ariaLabel);
  if (options.slider !== false) wrap.append(slide);

  function paint(): void {
    // Never rewrite the field while the shopper is typing in it, or the caret
    // jumps to the end after every keystroke.
    if (document.activeElement !== field) field.value = formatValue(current, range.decimals);
    slide.value = String(current);
    less.disabled = current <= range.min + 1e-9;
    more.disabled = current >= range.max - 1e-9;
  }

  function commit(next: number): void {
    const value = clampToStep(next, range);
    current = value;
    paint();
    options.onCommit(value);
  }

  less.addEventListener('click', () => commit(stepValue(current, range, -1)));
  more.addEventListener('click', () => commit(stepValue(current, range, 1)));

  field.addEventListener('change', () => {
    const typed = parseValue(field.value);
    if (typed === undefined) {
      paint();
      return;
    }
    commit(typed);
  });
  field.addEventListener('keydown', (event) => {
    const key = (event as KeyboardEvent).key;
    if (key === 'Enter') {
      event.preventDefault();
      field.blur();
      return;
    }
    if (key !== 'ArrowUp' && key !== 'ArrowDown') return;
    event.preventDefault();
    commit(stepValue(current, range, key === 'ArrowUp' ? 1 : -1));
  });

  slide.addEventListener('input', () => {
    const raw = Number(slide.value);
    if (!Number.isFinite(raw)) return;
    current = clampToStep(raw, range);
    if (document.activeElement !== field) field.value = formatValue(current, range.decimals);
    options.onInput?.(current);
  });
  slide.addEventListener('change', () => {
    const raw = Number(slide.value);
    commit(Number.isFinite(raw) ? raw : current);
  });

  paint();

  return {
    element: wrap,
    setValue(value) {
      current = clampToStep(value, range);
      paint();
    },
    value: () => current,
    destroy: () => wrap.remove(),
  };
}
